// frontend/src/components/modals/ImageUploadModal.jsx
import React, { useState, useEffect } from "react";
import { toast } from 'react-toastify';
import "./imageUploadModal.css";
import axios from "axios";

const ImageUploadModal = ({
  isOpen,
  onClose,
  onInsert,
  initialData = {},
  isEditMode = false,
}) => {
  const [file, setFile] = useState(null);
  const [alt, setAlt] = useState("");
  const [attribution, setAttribution] = useState("");
  const [caption, setCaption] = useState("");
  const [existingSrc, setExistingSrc] = useState("");

  useEffect(() => {
    setAlt(initialData?.alt || "");
    setAttribution(initialData?.attribution || "");
    setCaption(initialData?.caption || "");
    setExistingSrc(initialData?.src || "");
    setFile(null);
  }, [initialData]);

  const handleUpload = async () => {
    if (!alt.trim()) {
      toast.error("Alt text is required for accessibility.");
      return;
    }

    if (!attribution.trim()) {
      toast.error("Attribution is required.");
      return;
    }

    // File validation if uploading a new image
    if (file) {
      const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
      const allowedTypes = ["image/jpeg", "image/png", "image/webp"];

      if (!file) {
        toast.error("Please select an image.");
        return;
      }

      if (file.size > MAX_FILE_SIZE) {
        toast.error("Image is too large. Maximum file size is 5MB.");
        return;
      }

      if (!allowedTypes.includes(file.type)) {
        toast.error("Only JPG, PNG, and WebP images are supported.");
        return;
      }

      const formData = new FormData();
      formData.append("file", file);

      try {
        const res = await axios.post("/upload", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });

        const { original, variants, filenameBase } = res.data;

        onInsert({
          src: original,
          variants,
          filenameBase,
          alt,
          attribution,
          caption,
        });

        onClose();
      } catch (err) {
        console.error("Upload error", err);
        const errorMessage =
          err.response?.data?.error || "Error uploading image.";
        toast.error(errorMessage);
      }

      return;
    }

    // No new file: reuse existing image
    if (existingSrc) {

      onInsert({
        src: existingSrc,
        variants: initialData.variants || [],
        filenameBase: initialData.filenameBase || "",
        alt,
        attribution,
        caption,
      });

      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="image-upload-modal">
      <div className="modal-content">
        <h3>{isEditMode ? "Edit Image" : "Insert Image"}</h3>
        <input type="file" accept="image/*" onChange={(e) => setFile(e.target.files[0])} />
        <p className="image-hint">
          *Max upload size: 5MB.
        </p>
        <input
          type="text"
          placeholder="Alt Text *"
          value={alt}
          required
          onChange={(e) => setAlt(e.target.value)}
        />
        <input
          type="text"
          placeholder="Attribution *"
          value={attribution}
          required
          onChange={(e) => setAttribution(e.target.value)}
        />
        <input
          type="text"
          placeholder="Caption"
          value={caption}
          onChange={(e) => setCaption(e.target.value)}
        />
        {file ? (
          <img src={URL.createObjectURL(file)} alt="preview" className="preview" />
        ) : existingSrc ? (
          <img src={existingSrc} alt="preview" className="preview" />
        ) : null}
        <div
          style={{
            fontSize: "12px",
            fontWeight: "100",
            color: "grey",
          }}
        >
          * Required
        </div>
        <div className="modal-buttons">
          <button
            onClick={handleUpload}
            disabled={!alt.trim() || !attribution.trim() || (!file && !existingSrc)}
          >
            {isEditMode ? "Update" : "Insert"}
          </button>
          <button onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
};

export default ImageUploadModal;
